// Расписание по Ташкенту — секция внутри «Агент → Задачи» (вкладки агента: 7 → 5, разбор 26.09): компактный
// список — что, когда и следующий запуск; время и дни меняются по клику на строку, «Запустить сейчас» — в меню «⋯».
// Рядом — проверки, которые агент назначил себе сам, и правила очереди: пауза, тихие часы, расход подписки.
// Старая ссылка «/agent/schedule» ведёт сюда (ScheduleTab).
import { Button, Card, Chip, inTime, Popover, pct, toast, when } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate } from '@tanstack/react-router';
import { Ellipsis, Pause, Pencil, Play } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { QueueConfig, SchedulesResponse, ScheduleView } from '../../agent-api';
import { api } from '../../api';
import { SubscriptionUsage } from './SubscriptionUsage';

const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function describe(s: ScheduleView): string {
  if (!s.simple) return s.cron;
  if (s.simple.monthDay) return `${s.simple.monthDay}-го числа в ${s.simple.time}`;
  if (s.simple.days.length === 0) return `каждый день в ${s.simple.time}`;
  return `${s.simple.days.map((d) => DAYS[d - 1]).join(', ')} в ${s.simple.time}`;
}

/** Меню строки расписания: запустить сейчас, изменить время. */
function RowMenu({ s, onRun, onEdit }: { s: ScheduleView; onRun: () => void; onEdit?: () => void }) {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const pick = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };
  return (
    <>
      <button
        ref={btn}
        type="button"
        className="icon-btn"
        aria-label={`Ещё: ${s.title}`}
        title="Ещё"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((x) => !x)}
      >
        <Ellipsis />
      </button>
      <Popover
        anchor={btn}
        open={open}
        onClose={() => setOpen(false)}
        label={s.title}
        align="end"
        width={230}
      >
        <div role="menu" aria-label={s.title}>
          <button type="button" role="menuitem" className="pick-opt" onClick={pick(onRun)}>
            <span className="flex items-center gap-2">
              <Play size={16} /> Запустить сейчас
            </span>
          </button>
          {onEdit && (
            <button type="button" role="menuitem" className="pick-opt" onClick={pick(onEdit)}>
              <span className="flex items-center gap-2">
                <Pencil size={16} /> Изменить время и дни
              </span>
            </button>
          )}
        </div>
      </Popover>
    </>
  );
}

function ScheduleRow({ s, onSaved }: { s: ScheduleView; onSaved: () => void }) {
  const [edit, setEdit] = useState(false);
  const [time, setTime] = useState(s.simple?.time ?? '');
  const [days, setDays] = useState<number[]>(s.simple?.days ?? []);
  useEffect(() => {
    setTime(s.simple?.time ?? '');
    setDays(s.simple?.days ?? []);
  }, [s.simple?.time, s.simple?.days]);
  const save = async (patch: Record<string, unknown>) => {
    try {
      await api(`/api/agent/schedules/${s.id}`, patch, 'PATCH');
      toast('Расписание сохранено');
      onSaved();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const runNow = async () => {
    try {
      await api(`/api/agent/schedules/${s.id}/run`, {});
      toast(`«${s.title}» — в очереди`);
      onSaved();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const monthly = Boolean(s.simple?.monthDay);
  const toggleDay = (d: number) => {
    const next = days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort();
    setDays(next);
    void save({ time, days: next });
  };
  const editorId = `sch-edit-${s.id}`;
  return (
    <li className="sch-row" data-off={!s.enabled || undefined}>
      <input
        type="checkbox"
        checked={s.enabled}
        onChange={(e) => void save({ enabled: e.target.checked })}
        aria-label={`${s.title}: включено`}
      />
      <button
        type="button"
        className="sch-main"
        aria-expanded={edit}
        aria-controls={s.simple ? editorId : undefined}
        disabled={!s.simple}
        onClick={() => setEdit((x) => !x)}
      >
        <b>{s.title}</b>
        <span>
          {describe(s)}
          {s.enabled && s.nextAt ? ` · следующий ${when(s.nextAt)}` : s.enabled ? '' : ' · выключено'}
        </span>
      </button>
      <RowMenu s={s} onRun={() => void runNow()} onEdit={s.simple ? () => setEdit(true) : undefined} />
      {edit && s.simple && (
        <div className="sch-edit" id={editorId}>
          <input
            type="time"
            className="input"
            value={time}
            aria-label="Время по Ташкенту"
            onChange={(e) => setTime(e.target.value)}
            onBlur={() =>
              time &&
              time !== s.simple?.time &&
              void save({ time, ...(monthly ? { monthDay: s.simple?.monthDay } : { days }) })
            }
          />
          {!monthly && (
            <fieldset className="sch-days" aria-label="Дни недели (ничего не выбрано — каждый день)">
              {DAYS.map((d, i) => (
                <button
                  key={d}
                  type="button"
                  aria-pressed={days.includes(i + 1)}
                  onClick={() => toggleDay(i + 1)}
                >
                  {d}
                </button>
              ))}
            </fieldset>
          )}
          <Button size="sm" variant="ghost" onClick={() => setEdit(false)}>
            Готово
          </Button>
        </div>
      )}
    </li>
  );
}

function QueueSettings() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['agent', 'queue'],
    queryFn: () => api<{ config: QueueConfig }>('/api/agent/queue'),
  });
  const cfg = q.data?.config;
  const [quiet, setQuiet] = useState<{ from: string; to: string }>({ from: '', to: '' });
  useEffect(() => setQuiet(cfg?.quiet ?? { from: '', to: '' }), [cfg?.quiet]);
  const save = async (next: QueueConfig) => {
    try {
      await api('/api/agent/queue/config', next, 'PUT');
      void qc.invalidateQueries({ queryKey: ['agent', 'queue'] });
      toast('Сохранено');
    } catch (e) {
      toast((e as Error).message);
    }
  };
  if (!cfg) return <Card title="Очередь">{q.isPending ? <p className="muted">Загружаю…</p> : null}</Card>;
  return (
    <Card
      title="Очередь и лимиты подписки"
      action={
        <Button
          size="sm"
          variant={cfg.paused ? 'primary' : 'default'}
          icon={cfg.paused ? <Play /> : <Pause />}
          onClick={() => void save({ ...cfg, paused: !cfg.paused })}
        >
          {cfg.paused ? 'Снять с паузы' : 'Поставить на паузу'}
        </Button>
      }
    >
      <p className="muted" style={{ margin: '0 0 12px', fontSize: 'var(--fs-sm)' }}>
        Ваш чат с агентом всегда вне очереди. Фоновая работа ждёт, когда подписка близко к лимиту, и
        продолжается сама после сброса окна — ничего не теряется.
      </p>
      <dl className="facts">
        <div>
          <dt>Состояние</dt>
          <dd>
            {cfg.paused ? (
              <Chip tone="warning">На паузе — работа по расписанию ждёт</Chip>
            ) : (
              <Chip tone="success">Работает</Chip>
            )}
          </dd>
        </div>
        <div>
          <dt>Одновременно</dt>
          <dd>
            <select
              className="input"
              aria-label="Сколько фоновых запусков одновременно"
              value={cfg.maxBackground}
              onChange={(e) => void save({ ...cfg, maxBackground: Number(e.target.value) })}
            >
              {[1, 2, 3].map((n) => (
                <option key={n} value={n}>
                  {n} {n === 1 ? 'запуск' : 'запуска'}
                </option>
              ))}
            </select>
            <small>
              Плюс отдельное место для обходов, тревог и проверок; долгая работа — не больше одной.
            </small>
          </dd>
        </div>
        <div>
          <dt>Пауза у лимита</dt>
          <dd>
            по видам работ — в блоке «Расход подписки» ниже
            <small>Остаток подписки — для вашей работы в чате и в Claude Code.</small>
          </dd>
        </div>
        <div>
          <dt>Тихие часы</dt>
          <dd className="sch-quiet">
            <input
              type="time"
              className="input"
              aria-label="Тихие часы: с"
              value={quiet.from}
              onChange={(e) => setQuiet({ ...quiet, from: e.target.value })}
            />
            —
            <input
              type="time"
              className="input"
              aria-label="Тихие часы: до"
              value={quiet.to}
              onChange={(e) => setQuiet({ ...quiet, to: e.target.value })}
            />
            <Button
              size="sm"
              onClick={() => void save({ ...cfg, quiet: quiet.from && quiet.to ? quiet : null })}
              disabled={
                (quiet.from || null) === (cfg.quiet?.from ?? null) &&
                (quiet.to || null) === (cfg.quiet?.to ?? null)
              }
            >
              Сохранить
            </Button>
            {cfg.quiet && (
              <Button size="sm" variant="ghost" onClick={() => void save({ ...cfg, quiet: null })}>
                Убрать
              </Button>
            )}
          </dd>
        </div>
      </dl>
    </Card>
  );
}

/** «Через 3 ч» — только на ближайшие двое суток; дальше хватает даты («30 сентября, 09:40»). */
const soon = (iso: string) => new Date(iso).getTime() - Date.now() < 48 * 3_600_000;

/** Расписание, проверки агента, очередь и расход подписки — секция «Задач» (якорь #schedule). */
export function ScheduleSection() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['agent', 'schedules'],
    queryFn: () => api<SchedulesResponse>('/api/agent/schedules'),
    refetchInterval: 30_000,
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['agent'] });
  const cancel = async (id: number) => {
    try {
      await api(`/api/agent/followups/${id}/cancel`, {});
      refresh();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const rel = q.data?.reliability;
  // Пришли по ссылке «/agent/schedule» или «Расписание» со «Сводки» — показать секцию, когда она отрисовалась
  const section = useRef<HTMLElement>(null);
  const ready = q.isSuccess;
  useEffect(() => {
    if (ready && window.location.hash === '#schedule') section.current?.scrollIntoView({ block: 'start' });
  }, [ready]);
  return (
    <section id="schedule" ref={section} className="sch-sec" aria-labelledby="sch-h">
      <h2 id="sch-h" className="dec-h">
        Расписание и очередь
      </h2>
      <div className="sch-grid">
        <Card
          title="Расписание по Ташкенту"
          action={
            rel && rel.total > 0 ? (
              <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>
                за 7 дней успешно {rel.ok} из {rel.total} ({pct((rel.ok / rel.total) * 100, 0)})
              </span>
            ) : undefined
          }
        >
          {q.isPending && <p className="muted">Загружаю…</p>}
          <ul className="sch-list">
            {q.data?.schedules.map((s) => (
              <ScheduleRow key={s.id} s={s} onSaved={refresh} />
            ))}
          </ul>
        </Card>
        <Card title="Проверки, которые агент назначил себе" count={q.data?.followups.length ?? 0}>
          {q.data && q.data.followups.length === 0 && (
            <p className="muted" style={{ margin: 0 }}>
              Пока нет. Агент назначает их сам: «запустил адсет — гляну через 2 часа».
            </p>
          )}
          <ul className="fu-list">
            {q.data?.followups.map((f) => (
              <li key={f.id}>
                <div className="min-w-0">
                  <b>{when(f.dueAt)}</b>
                  {soon(f.dueAt) && <span className="muted"> · {inTime(f.dueAt)}</span>}
                  <p>{f.prompt}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => void cancel(f.id)}>
                  Отменить
                </Button>
              </li>
            ))}
          </ul>
        </Card>
        <QueueSettings />
        <SubscriptionUsage />
      </div>
    </section>
  );
}

/** Старая вкладка «Расписание» — теперь секция «Задач». */
export function ScheduleTab() {
  return <Navigate to="/agent/tasks" hash="schedule" replace />;
}
